import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import { Input } from '../common/Input';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import logger from '../../utils/logger';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`;

const StyledInput = styled(Input)`
  padding-left: 2.5rem;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const ForgotPassword = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.85rem;
  text-align: right;
  cursor: pointer;
  margin-top: -1rem;
  padding: 0;
  text-decoration: underline;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => `${theme.colors.success}10`};
  border-radius: 4px;
  text-align: center;
`;

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ 
  onSwitchToRegister,
  onSwitchToForgotPassword
}) => {
  const { signIn, error, loading, clearError, supabaseUser, session } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ email?: string; password?: string }>({});
  const [loginSuccess, setLoginSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Effet pour rediriger l'utilisateur après connexion réussie
  useEffect(() => {
    if (supabaseUser && session && !loading) {
      // Récupérer l'URL d'origine si elle existe
      const from = location.state?.from || '/';
      
      logger.info('Utilisateur connecté, redirection vers:', from);
      setLoginSuccess(true);
      
      // Rediriger vers la page d'origine après un court délai
      const redirectTimer = setTimeout(() => {
        navigate(from, { replace: true });
      }, 1000);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [supabaseUser, session, loading, navigate, location.state]);

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    
    if (!email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'L\'email est invalide';
    }
    
    if (!password) {
      errors.password = 'Le mot de passe est requis';
    } else if (password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLoginSuccess(false);
    
    if (validateForm()) {
      try {
        logger.debug('Tentative de connexion avec:', { email });
        await signIn(email, password);
      } catch (err) {
        logger.error('Erreur lors de la connexion:', err);
      }
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <h2>Connexion</h2>
      
      <FormGroup>
        <Label htmlFor="email">Email</Label>
        <InputWrapper>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          <StyledInput
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre adresse email"
            $hasError={!!validationErrors.email}
          />
        </InputWrapper>
        {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="password">Mot de passe</Label>
        <InputWrapper>
          <IconWrapper>
            <FaLock />
          </IconWrapper>
          <StyledInput
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Votre mot de passe"
            $hasError={!!validationErrors.password}
          />
        </InputWrapper>
        {validationErrors.password && <ErrorMessage>{validationErrors.password}</ErrorMessage>}
      </FormGroup>
      
      <ForgotPassword type="button" onClick={onSwitchToForgotPassword}>
        Mot de passe oublié ?
      </ForgotPassword>
      
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      
      {loginSuccess && (
        <SuccessMessage>
          Connexion réussie ! Redirection en cours...
        </SuccessMessage>
      )}
      
      <ButtonsContainer>
        <Button type="submit" $variant="primary" disabled={loading}>
          {loading ? 'Connexion en cours...' : 'Se connecter'}
        </Button>
        <Button type="button" $variant="text" onClick={onSwitchToRegister}>
          Pas encore de compte ? S'inscrire
        </Button>
      </ButtonsContainer>
    </FormContainer>
  );
};

export default LoginForm;

